import { DropdownSelect } from '@/components/ui/dropdown-select';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { useOpenSnapshot } from '@/hooks/use-open-snapshot';
import { cn } from '@/lib/cn';
import { orderSelectedFirst, sortByLabel } from '@/utils/order-selected-first';
import { isEmpty } from 'lodash';
import { Info } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';
import type * as React from 'react';
import type { GroupBase, Props } from 'react-select';

interface FormSelectProps {
  name: string;
  label?: string;
  optional?: boolean;
  required?: boolean;
  optionKey?: string;
  tooltip?: React.ReactNode;
  formWrapperClassName?: string;
}

function FormSelect<
  Option extends Record<string, unknown> = Record<string, unknown>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  name,
  label,
  optional,
  required,
  optionKey,
  tooltip,
  formWrapperClassName,
  ...props
}: Props<Option, IsMulti, Group> & FormSelectProps) {
  const form = useFormContext();

  // The selection the menu opened with, which is what its order follows.
  const pinned = useOpenSnapshot<unknown[] | null>(null);

  const labelOf = (option: Option) =>
    props.getOptionLabel?.(option) ?? String(option.label ?? option.name ?? '');

  // Grouped options keep the caller's grouping and order.
  const isGrouped = (props.options ?? []).some(
    item => typeof item === 'object' && item !== null && 'options' in item
  );

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field, fieldState }) => {
        const key = optionKey ?? 'id';
        const multiValues: unknown[] = Array.isArray(field.value)
          ? field.value
          : [];
        const pinnedSelection = pinned.snapshot ?? multiValues;

        // A multi-select lists its chosen options first, each half
        // alphabetical, and its chips alphabetical too.
        const orderedOptions =
          props.isMulti && !isGrouped
            ? orderSelectedFirst(
                (props.options ?? []) as Option[],
                opt => pinnedSelection.includes(opt[key]),
                labelOf
              )
            : props.options;

        const selectedOptions = props.isMulti
          ? field.value
            ? sortByLabel(
                (props.options as Option[]).filter(opt =>
                  multiValues.includes(opt[key])
                ),
                labelOf
              )
            : []
          : field.value !== undefined &&
              field.value !== null &&
              field.value !== ''
            ? ((props.options?.find(item => {
                const option = item as Option;
                return option[optionKey ?? ''] === field.value;
              }) as Option) ?? null)
            : null;
        return (
          <div
            className={cn('flex w-full flex-col gap-1.5', formWrapperClassName)}
          >
            {!isEmpty(label) && (
              <label
                htmlFor={name}
                className="text-text-secondary flex items-center gap-1 text-sm"
              >
                {label}
                {optional && (
                  <span className="font-outfit text-text-muted text-xs">
                    (Optional)
                  </span>
                )}
                {required && <span className="text-feedback-error">*</span>}
                {tooltip && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="size-3.5" />
                    </TooltipTrigger>
                    <TooltipContent className="text-text-tertiary max-w-sm text-sm drop-shadow-lg">
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                )}
              </label>
            )}
            <DropdownSelect
              isError={fieldState.invalid}
              value={selectedOptions}
              {...props}
              options={orderedOptions}
              onMenuOpen={() => {
                pinned.capture(multiValues);
                props.onMenuOpen?.();
              }}
              onMenuClose={() => {
                pinned.release();
                props.onMenuClose?.();
              }}
              onChange={(e, actionValue) => {
                if (props.isMulti) {
                  const value = (e as Option[])?.map(
                    option => option[optionKey ?? '']
                  );

                  field.onChange(value);
                } else {
                  const option = e as Option;
                  field.onChange(option ? option[optionKey ?? ''] : null);
                }
                props.onChange?.(e, actionValue);
              }}
            />
            {fieldState.invalid && (
              <span data-field-error className="text-xs text-red-500">
                {fieldState.error?.message}
              </span>
            )}
          </div>
        );
      }}
    />
  );
}

export { FormSelect };
